import {
    Box,
    Text,
    Grid,
    Flex,
    Button,
    Dialog,
    Portal,
    CloseButton,
} from "@chakra-ui/react";

import {
    Upload,
    X,
    FileText,
} from "lucide-react";

import {
    useRef,
    useEffect,
    useState,
} from "react";

const AttachmentUpload = ({
    files = [],
    onChange,
    maxFiles = 6,
    accept = "*",
    title = "Attachments",
    description = "Upload files",
}) => {
    const inputRef = useRef(null);

    const [isOpen, setIsOpen] = useState(false);

    // Clean up generated preview URLs when the component unmounts.
    useEffect(() => {
        return () => {
            files.forEach((item) => {
                if (item.preview) {
                    URL.revokeObjectURL(item.preview);
                }
            });
        };
    }, [files]);

    // Open the file picker after the instructions dialog closes.
    const handleChooseFile = () => {
        setIsOpen(false);

        setTimeout(() => {
            inputRef.current?.click();
        }, 200);
    };

    // Process selected files.
    const handleChange = (e) => {
        const selected = Array.from(
            e.target.files || []
        );

        const processed = selected.map((file) => ({
            file,
            preview: file.type.startsWith("image/")
                ? URL.createObjectURL(file)
                : null,
        }));

        if (maxFiles === 1) {
            // Single-file mode: replace the existing file.
            files.forEach((item) => {
                if (item.preview) {
                    URL.revokeObjectURL(item.preview);
                }
            });

            onChange(processed.slice(0, 1));
        } else {
            // Multiple-file mode: append up to the limit.
            const remainingSlots = Math.max(
                0,
                maxFiles - files.length
            );

            const filesToAdd = processed.slice(
                0,
                remainingSlots
            );

            // Clean up previews for files that exceed the limit.
            processed.slice(remainingSlots).forEach((item) => {
                if (item.preview) {
                    URL.revokeObjectURL(item.preview);
                }
            });

            onChange([
                ...files,
                ...filesToAdd,
            ]);
        }

        // Allow the same file to be selected again.
        e.target.value = "";
    };

    // Remove a selected file.
    const removeFile = (index) => {
        const item = files[index];

        if (item?.preview) {
            URL.revokeObjectURL(item.preview);
        }

        onChange(
            files.filter((_, i) => i !== index)
        );
    };

    const acceptedFormats =
        accept === "*"
            ? "All file types"
            : accept
                  .split(",")
                  .map((format) => format.trim())
                  .join(", ");

    return (
        <Box>
            {/* Upload title */}
            <Text
                fontSize="xs"
                fontWeight="700"
                color="gray.500"
                letterSpacing="1px"
                mb={2}
            >
                {title.toUpperCase()}
            </Text>

            {/* Hidden native file input */}
            <input
                hidden
                ref={inputRef}
                type="file"
                multiple={maxFiles > 1}
                accept={accept}
                onChange={handleChange}
            />

            {/* Upload area */}
            <Box
                border="2px dashed"
                borderColor="gray.300"
                borderRadius="12px"
                bg="#F8FAFC"
                py={12}
                px={4}
                textAlign="center"
                cursor="pointer"
                transition="all 0.2s"
                onClick={() => setIsOpen(true)}
                _hover={{
                    bg: "#F5F8FF",
                    borderColor: "#2453FF",
                }}
            >
                <Flex
                    justify="center"
                    mb={3}
                >
                    <Upload
                        size={28}
                        color="#6B7280"
                    />
                </Flex>

                <Text fontWeight="600">
                    {description}
                </Text>

                <Text
                    mt={1}
                    color="gray.500"
                    fontSize="sm"
                >
                    {maxFiles} file
                    {maxFiles > 1 ? "s" : ""} maximum
                </Text>
            </Box>

            {/* Selected file previews */}
            {files.length > 0 && (
                <Grid
                    templateColumns={{
                        base: "1fr",
                        sm: "repeat(2, minmax(0, 1fr))",
                    }}
                    gap={3}
                    mt={4}
                >
                    {files.map((item, index) => (
                        <Box
                            key={`${item.file.name}-${index}`}
                            border="1px solid"
                            borderColor="gray.200"
                            borderRadius="12px"
                            p={3}
                            minW={0}
                        >
                            <Flex
                                gap={3}
                                align="center"
                            >
                                {item.preview ? (
                                    <Box
                                        w="70px"
                                        h="70px"
                                        flexShrink={0}
                                        borderRadius="10px"
                                        overflow="hidden"
                                    >
                                        <img
                                            src={item.preview}
                                            alt={item.file.name}
                                            style={{
                                                width: "100%",
                                                height: "100%",
                                                objectFit: "cover",
                                            }}
                                        />
                                    </Box>
                                ) : (
                                    <Flex
                                        w="70px"
                                        h="70px"
                                        flexShrink={0}
                                        bg="gray.100"
                                        borderRadius="10px"
                                        justify="center"
                                        align="center"
                                    >
                                        <FileText size={28} />
                                    </Flex>
                                )}

                                <Box
                                    flex="1"
                                    minW={0}
                                >
                                    <Text
                                        fontWeight="600"
                                        fontSize="sm"
                                        overflow="hidden"
                                        textOverflow="ellipsis"
                                        whiteSpace="nowrap"
                                    >
                                        {item.file.name}
                                    </Text>

                                    <Text
                                        color="gray.500"
                                        fontSize="xs"
                                    >
                                        {(
                                            item.file.size / 1024
                                        ).toFixed(1)}{" "}
                                        KB
                                    </Text>
                                </Box>

                                <Box
                                    as="button"
                                    type="button"
                                    aria-label={`Remove ${item.file.name}`}
                                    flexShrink={0}
                                    cursor="pointer"
                                    p={1}
                                    onClick={() => removeFile(index)}
                                >
                                    <X
                                        size={16}
                                        color="#EF4444"
                                    />
                                </Box>
                            </Flex>
                        </Box>
                    ))}
                </Grid>
            )}

            {/* Chakra UI v3 instructions dialog */}
            <Dialog.Root
                open={isOpen}
                onOpenChange={(e) => setIsOpen(e.open)}
                placement="center"
                size="md"
                closeOnEscape
                closeOnInteractOutside
            >
                <Portal>
                    <Dialog.Backdrop />

                    <Dialog.Positioner>
                        <Dialog.Content
                            mx={4}
                            borderRadius="20px"
                        >
                            <Dialog.Header>
                                <Flex
                                    align="center"
                                    gap={3}
                                    pr={6}
                                >
                                    <Flex
                                        w="44px"
                                        h="44px"
                                        flexShrink={0}
                                        bg="#F5F8FF"
                                        color="#2453FF"
                                        borderRadius="12px"
                                        align="center"
                                        justify="center"
                                    >
                                        <Upload size={22} />
                                    </Flex>

                                    <Box>
                                        <Dialog.Title>
                                            {title}
                                        </Dialog.Title>

                                        <Text
                                            fontSize="sm"
                                            color="gray.500"
                                            fontWeight="normal"
                                        >
                                            Please review before uploading
                                        </Text>
                                    </Box>
                                </Flex>
                            </Dialog.Header>

                            <Dialog.CloseTrigger asChild>
                                <CloseButton
                                    size="sm"
                                    position="absolute"
                                    top={3}
                                    right={3}
                                />
                            </Dialog.CloseTrigger>

                            <Dialog.Body>
                                {/* Description passed through props */}
                                <Box
                                    bg="gray.50"
                                    border="1px solid"
                                    borderColor="gray.200"
                                    borderRadius="12px"
                                    p={4}
                                >
                                    <Text
                                        fontSize="sm"
                                        color="gray.700"
                                        lineHeight="1.8"
                                    >
                                        {description}
                                    </Text>
                                </Box>

                                {/* File requirements */}
                                <Flex
                                    mt={4}
                                    gap={3}
                                    align="flex-start"
                                >
                                    <FileText
                                        size={18}
                                        color="#6B7280"
                                        style={{
                                            flexShrink: 0,
                                            marginTop: "2px",
                                        }}
                                    />

                                    <Box>
                                        <Text
                                            fontSize="sm"
                                            fontWeight="600"
                                        >
                                            File requirements
                                        </Text>

                                        <Text
                                            fontSize="sm"
                                            color="gray.500"
                                            mt={1}
                                        >
                                            Accepted formats: {acceptedFormats}
                                        </Text>

                                        <Text
                                            fontSize="sm"
                                            color="gray.500"
                                        >
                                            Maximum: {maxFiles} file
                                            {maxFiles > 1 ? "s" : ""}
                                        </Text>
                                    </Box>
                                </Flex>
                            </Dialog.Body>

                            <Dialog.Footer gap={3}>
                                <Button
                                    variant="outline"
                                    onClick={() => setIsOpen(false)}
                                >
                                    Cancel
                                </Button>

                                <Button
                                    bg="#2453FF"
                                    color="white"
                                    _hover={{
                                        bg: "#1D43D8",
                                    }}
                                    onClick={handleChooseFile}
                                >
                                    <Upload size={17} />
                                    Choose File
                                </Button>
                            </Dialog.Footer>
                        </Dialog.Content>
                    </Dialog.Positioner>
                </Portal>
            </Dialog.Root>
        </Box>
    );
};

export default AttachmentUpload;
